// Host-internal G08 command boundary. Rendering and product rules are injected.
// Existing owner commands / host history alone own mutation, undo and revision.
export const PLAN_EDIT_LIMITS = Object.freeze({ targets: 10000, nodes: 250000, characters: 8 * 1024 * 1024, depth: 64 });
const ACTIONS = new Set(['copy', 'paste', 'delete', 'nudge', 'undo', 'redo']);
const HISTORY = new Set(['undo', 'redo']);
const CONTEXT_KEYS = ['projectToken', 'sheetId', 'view', 'epoch', 'modelRevision', 'geometryRevision', 'assetRevision', 'tool'];
const id = value => typeof value === 'string' && value.length > 0 && value.length <= 256 && value.trim() === value;
const reply = (status, code) => Object.freeze({ status, ...(code ? { code } : {}) });
function sync(value) {
  if (value && typeof value.then === 'function') {
    Promise.resolve(value).catch(() => undefined);
    throw new TypeError('Plan edit ports must complete synchronously');
  }
  return value;
}

/** Bounded JSON ownership transfer; reject getters, exotic objects and nonfinite data. */
export function copyPlanEditData(source) {
  let nodes = 0, characters = 0;
  const ancestors = new Set();
  function visit(value, depth) {
    if (++nodes > PLAN_EDIT_LIMITS.nodes || depth > PLAN_EDIT_LIMITS.depth) throw new RangeError('Plan edit data budget exceeded');
    if (typeof value === 'string') {
      characters += value.length;
      if (characters > PLAN_EDIT_LIMITS.characters) throw new RangeError('Plan edit text budget exceeded');
      return value;
    }
    if (value === null || typeof value === 'boolean' || typeof value === 'number' && Number.isFinite(value)) return value;
    if (!value || typeof value !== 'object' || ancestors.has(value)) throw new TypeError('Plan edit requires finite JSON data');
    const array = Array.isArray(value), proto = Object.getPrototypeOf(value);
    if (array ? proto !== Array.prototype : proto !== Object.prototype && proto !== null) throw new TypeError('Plan edit requires plain data');
    if (array && value.length > PLAN_EDIT_LIMITS.nodes) throw new RangeError('Plan edit array budget exceeded');
    ancestors.add(value);
    const result = array ? [] : {}, keys = Reflect.ownKeys(value);
    if (keys.length > PLAN_EDIT_LIMITS.nodes || array && keys.length !== value.length + 1) throw new TypeError('Invalid plan edit array or record');
    for (const key of keys) {
      if (array && key === 'length') continue;
      const property = Object.getOwnPropertyDescriptor(value, key);
      if (typeof key !== 'string' || ['__proto__', 'prototype', 'constructor'].includes(key)
        || !property?.enumerable || !Object.hasOwn(property, 'value')
        || array && (!/^(0|[1-9]\d*)$/.test(key) || Number(key) >= value.length)) throw new TypeError('Invalid plan edit property');
      characters += key.length;
      if (characters > PLAN_EDIT_LIMITS.characters) throw new RangeError('Plan edit text budget exceeded');
      result[key] = visit(property.value, depth + 1);
    }
    ancestors.delete(value);
    return Object.freeze(result);
  }
  return visit(source, 0);
}

export function readPlanEditContext(getContext) {
  const source = sync(getContext());
  if (!source?.projectToken || !id(source.sheetId) || source.view !== 'plan') return null;
  if (!['epoch', 'modelRevision', 'geometryRevision', 'assetRevision'].every(key => Number.isSafeInteger(source[key]) && source[key] >= 0)) {
    throw new TypeError('Plan edit requires explicit context revisions');
  }
  return Object.freeze(Object.fromEntries(CONTEXT_KEYS.map(key => [key, key === 'tool' ? source[key] ?? null : source[key]])));
}
export const samePlanEditContext = (a, b) => a && b && CONTEXT_KEYS.every(key => a[key] === b[key]);
export function copyPlanEditTargets(source, owner) {
  if (!Array.isArray(source) || source.length > PLAN_EDIT_LIMITS.targets) throw new TypeError('Invalid plan edit selection');
  const targets = copyPlanEditData(source), seen = new Set();
  for (const target of targets) {
    if (!target || !id(target.id) || !id(target.kind) || target.owner !== owner
      || Object.keys(target).some(key => !['id', 'kind', 'owner', 'meta'].includes(key))) throw new TypeError('Invalid or foreign plan edit target');
    const key = JSON.stringify([target.owner, target.kind, target.id]);
    if (seen.has(key)) throw new TypeError('Duplicate plan edit target');
    seen.add(key);
  }
  return targets;
}
function payloadOf(action, source, owner) {
  const payload = copyPlanEditData(source);
  if (!payload || typeof payload !== 'object' || Array.isArray(payload)) throw new TypeError('Invalid plan edit payload');
  const allowed = action === 'nudge' ? ['dx', 'dz'] : action === 'paste' ? ['clipboard', 'delta'] : [];
  if (Object.keys(payload).some(key => !allowed.includes(key))) throw new TypeError('Unexpected plan edit payload');
  const delta = action === 'nudge' ? payload : payload.delta;
  if (delta !== undefined && (!delta || typeof delta !== 'object' || Array.isArray(delta)
    || Object.keys(delta).length !== 2 || !['dx', 'dz'].every(key => typeof delta[key] === 'number' && Number.isFinite(delta[key]) && Math.abs(delta[key]) <= 1e9))) {
    throw new TypeError('Invalid plan edit displacement');
  }
  if (action === 'paste' && payload.clipboard?.owner !== owner) throw new TypeError('Foreign or missing plan clipboard');
  return payload;
}

/** One pending, single-use request. Capture after any async UI work, or let it expire.
 * This is a host-only coordinator, not a sandbox or an alternate history stack.
 */
export function createPlanEditCommands({ owner, getContext, getSelection, canExecute, adapter, history } = {}) {
  if (!id(owner) || ![getContext, getSelection, canExecute, adapter?.supports, adapter?.execute].every(fn => typeof fn === 'function')) {
    throw new TypeError('Plan edit requires an owner and explicit command ports');
  }
  let pending = null, disposed = false, executing = false, preparing = false, generation = 0;
  const access = action => sync(canExecute(action)) === true;
  const supported = (action, targets, payload) => HISTORY.has(action)
    ? typeof history?.[action] === 'function' && typeof history?.[action === 'undo' ? 'canUndo' : 'canRedo'] === 'function'
      && sync(history[action === 'undo' ? 'canUndo' : 'canRedo']()) === true
    : sync(adapter.supports(action, targets, payload)) === true;
  function prepare(action, source = {}) {
    if (disposed || executing || preparing) return reply('blocked');
    pending = null;
    if (!ACTIONS.has(action)) return reply('unsupported');
    preparing = true; const capturedGeneration = generation;
    try {
      const captured = readPlanEditContext(getContext);
      if (!captured || !access(action)) return reply('blocked');
      const targets = HISTORY.has(action) ? Object.freeze([]) : copyPlanEditTargets(sync(getSelection()), owner);
      const payload = payloadOf(action, source, owner);
      if (!HISTORY.has(action) && action !== 'paste' && !targets.length) return reply('invalid-target');
      if (!supported(action, targets, payload)) return reply('unsupported');
      if (disposed || generation !== capturedGeneration || !samePlanEditContext(captured, readPlanEditContext(getContext))
        || !HISTORY.has(action) && JSON.stringify(copyPlanEditTargets(sync(getSelection()), owner)) !== JSON.stringify(targets)) return reply('stale');
      const token = Object.freeze({});
      pending = { token, action, captured, capturedGeneration, targets, payload, selectionKey: JSON.stringify(targets) };
      return Object.freeze({ status: 'ready', token });
    } catch { return reply('rejected', 'PLAN_EDIT_PREPARATION_FAILED'); }
    finally { preparing = false; }
  }
  function execute(token) {
    const request = pending;
    if (disposed || executing || preparing || !request || request.token !== token) return reply('stale');
    pending = null; executing = true;
    try {
      const { action, captured, capturedGeneration, targets, payload } = request;
      if (!samePlanEditContext(captured, readPlanEditContext(getContext))) return reply('stale');
      if (!access(action)) return reply('blocked');
      if (!HISTORY.has(action) && JSON.stringify(copyPlanEditTargets(sync(getSelection()), owner)) !== request.selectionKey) return reply('stale');
      if (!supported(action, targets, payload)) return reply('unsupported');
      // Availability/resolver hooks must not redirect a prepared request.
      if (!access(action)) return reply('blocked');
      if (disposed || generation !== capturedGeneration || !samePlanEditContext(captured, readPlanEditContext(getContext))
        || !HISTORY.has(action) && JSON.stringify(copyPlanEditTargets(sync(getSelection()), owner)) !== request.selectionKey) return reply('stale');
      if (HISTORY.has(action)) return reply(sync(history[action]()) === true ? 'accepted' : 'noop');
      const result = copyPlanEditData(sync(adapter.execute(action, targets, payload)));
      if (!['accepted', 'noop', 'rejected', 'blocked', 'unsupported', 'invalid-target'].includes(result?.status)) throw new TypeError('Invalid plan edit outcome');
      return result;
    } catch { return reply('failed', 'PLAN_EDIT_EXECUTION_FAILED'); }
    finally { executing = false; }
  }
  return Object.freeze({ prepare, execute,
    invalidate() { generation++; pending = null; },
    dispose() { disposed = true; generation++; pending = null; },
    snapshot: () => Object.freeze({ owner, disposed, executing, pending: Boolean(pending) })
  });
}
